<template>
    <div :class="$style.container">
        <div :class="$style.rating_wrapper">
            <Stars :rating="props.game.rating" />
            <AgeRating :rating="props.game.age_rating" />
        </div>
        <template
            v-for="({ key, text, links }, index) in data"
            :key="index"
        >
            <div v-if="text || links?.length">
                <Typography
                    type="paragraph"
                    size="s"
                    :responsive="false"
                    :class="$style.key"
                >
                    {{ key }}:
                </Typography>
                <Typography
                    type="paragraph"
                    size="s"
                    :responsive="false"
                    :class="$style.value"
                >
                    <template v-if="links">
                        <template
                            v-for="(link, linkIndex) in links"
                            :key="linkIndex"
                        >
                            <Link
                                :to="link.to"
                                :externalTo="link.externalTo"
                                color="purple-600"
                                target="_blank"
                            >
                                {{ link.title }}
                            </Link>
                            <span>{{ linkIndex !== links.length - 1 ? ', ' : '' }}</span>
                        </template>
                    </template>
                    <template v-else-if="text">
                        {{ text }}
                    </template>
                </Typography>
            </div>
        </template>
    </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import Link from '@/components/Link.vue'
import Stars from '@/components/Stars.vue'
import Typography from '@/components/Typography.vue'
import { useTr } from '@/composables/use-tr'
import { mapLocaleToHrefLang } from '@/i18n/helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { getSubCategories } from '@/pages/Games/utils'
import { ROUTE_NAMES } from '@/router/constants'
import type { APIGameType, GameCategoriesInfoType } from '@/types'

import AgeRating from './AgeRating.vue'

const linksTypes2Titles: Record<string, string> = {
    WIKI: 'Wikipedia',
    APPLE_STORE: 'Apple App Store',
    GOOGLE_STORE: 'Google Play',
    FANDOM: 'Fandom',
    DISCORD: 'Discord',
    HUAWEI_STORE: 'Huawei AppGallery',
    XIAOMI_STORE: 'Xiaomi App Store',
    SAMSUNG_STORE: 'Samsung Galaxy Store',
    AMAZON_STORE: 'Amazon Appstore',
    UPTODOWN_STORE: 'Uptodown App Store',
    TAPTAP_STORE: 'TapTap',
}

const storeTypes = new Set([
    'APPLE_STORE',
    'GOOGLE_STORE',
    'HUAWEI_STORE',
    'XIAOMI_STORE',
    'SAMSUNG_STORE',
    'AMAZON_STORE',
    'UPTODOWN_STORE',
    'TAPTAP_STORE',
])
const socialTypes = new Set(['FANDOM', 'DISCORD'])
const wikiTypes = new Set(['WIKI'])
const otherTypes = new Set(['OTHER'])

const props = defineProps<{
    game: APIGameType
    categoriesInfo: GameCategoriesInfoType
}>()

const tr = useTr()

const { currentLocale } = useI18nAddon()

function getLocaleDate(inputDate: string) {
    return new Date(inputDate).toLocaleDateString(currentLocale.value, { month: 'long', year: 'numeric' })
}

function getExternalLinks(types: Set<string>) {
    return props.game.links
        .filter((link) => types.has(link.type))
        .map((link) => ({
            title: link.type === 'OTHER' ? link.title : linksTypes2Titles[link.type] || link.title,
            externalTo: link.url,
        }))
}

const supportedLanguages = computed(() => {
    const currentLanguage = mapLocaleToHrefLang(currentLocale.value)
    const translator = new Intl.DisplayNames([currentLanguage], { type: 'language' })

    return (
        props.game.supported_languages
            // chrome(and node) and safari has different letter capitalization
            // so need to normalize to avoid hydration mismatches
            ?.map((languageName) => translator.of(languageName.slice(0, 2)))
            .filter((x): x is string => !!x)
            .map((x) => x.charAt(0).toLocaleUpperCase() + x.slice(1))
            .join(', ') || ''
    )
})

const data = computed<
    { key: string; text?: string; links?: { title: string; to?: RouteLocationRaw; externalTo?: string }[] }[]
>(() => [
    {
        key: tr('gameDetails.developer'),
        links: props.game.developer_id
            ? [
                  {
                      title: props.game.developer || '',
                      to: { name: ROUTE_NAMES.DEVELOPER, params: { hru: props.game.developer_id } },
                  },
              ]
            : undefined,
        text: props.game.developer,
    },
    {
        key: tr('gameDetails.orientation'),
        text: [
            props.game.horizontal && tr('orientationLabel.horizontalOrientation'),
            props.game.vertical && tr('orientationLabel.verticalOrientation'),
        ]
            .filter(Boolean)
            .join(', '),
    },
    {
        key: tr('gameDetails.platforms'),
        text: [
            props.game.is_desktop && tr('gameContainer.desktop'),
            props.game.is_android && tr('gameContainer.android'),
            props.game.is_ios && tr('gameContainer.ios'),
        ]
            .filter(Boolean)
            .join(', '),
    },
    {
        key: tr('gameDetails.releaseDate'),
        text: getLocaleDate(props.game.createdAt),
    },
    {
        key: tr('gameDetails.lastUpdated'),
        text: getLocaleDate(props.game.updatedAt),
    },
    {
        key: tr('gameDetails.engine'),
        text: props.game.engine
            ?.map((name) => `${name.charAt(0).toUpperCase()}${name.slice(1).toLowerCase()}`)
            .join(', '),
    },
    {
        key: tr('gameDetails.supportedLanguages'),
        text: supportedLanguages.value,
    },
    {
        key: tr('gameDetails.storesLinks'),
        links: getExternalLinks(storeTypes),
    },
    {
        key: tr('gameDetails.wikiLinks'),
        links: getExternalLinks(wikiTypes),
    },
    {
        key: tr('gameDetails.otherLinks'),
        links: getExternalLinks(otherTypes),
    },
    {
        key: tr('gameDetails.socialLinks'),
        links: getExternalLinks(socialTypes),
    },
    {
        key: tr('gameDetails.series'),
        links: getSubCategories('series', props.categoriesInfo).map(({ title, href }) => ({ title, to: href })),
    },
    {
        key: tr('gameDetails.categories'),
        links: getSubCategories('category', props.categoriesInfo).map(({ title, href }) => ({ title, to: href })),
    },
    {
        key: tr('gameDetails.collections'),
        links: props.categoriesInfo.collectionsInfo.map(({ title, href }) => ({ title, to: href })),
    },
])
</script>
<style module>
.container {
    display: grid;
    gap: 8px 40px;
}

.key {
    color: var(--white-60);
}

.value {
    color: var(--white);
}

.value:has(a) {
    color: var(--purple-600);
}

.rating_wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
}
</style>
